import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AnalysisSummaryHeader = ({ summaryData, onRefresh, isLoading, documentBaseName }) => {
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getSeverityIcon = (type) => {
    const iconMap = {
      gap_detection: 'Search',
      inconsistencies: 'AlertTriangle',
      contradictions: 'XCircle',
      grammar_issues: 'Type',
      spelling_errors: 'Spellcheck',
      language_issues: 'Globe'
    };
    return iconMap?.[type] || 'AlertCircle';
  };

  const getTypeLabel = (type) => {
    const labelMap = {
      gap_detection: 'Gap Detection',
      inconsistencies: 'Inconsistencies',
      contradictions: 'Contradictions',
      grammar_issues: 'Grammar Issues',
      spelling_errors: 'Spelling Errors',
      language_issues: 'Language Issues'
    };
    return labelMap?.[type] || 'Issues';
  };

  const totalFindings = summaryData?.categories?.reduce((sum, cat) => sum + cat?.count, 0) || 0;
  const resolvedFindings = summaryData?.categories?.reduce((sum, cat) => sum + cat?.resolved, 0) || 0;
  const completionRate = totalFindings > 0 ? Math.round((resolvedFindings / totalFindings) * 100) : 0;

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-2">AI Document Analysis</h1>
          <p className="text-muted-foreground">
            Analyzing {summaryData?.documentsAnalyzed || 0} documents
            {documentBaseName && documentBaseName !== 'All Document Bases' && (
              <span> in <span className="font-medium text-foreground">{documentBaseName}</span></span>
            )}
          </p>
        </div>
        <Button
          onClick={onRefresh}
          disabled={isLoading}
          variant="outline"
          iconName={isLoading ? "Loader2" : "RefreshCw"}
          iconPosition="left"
          iconSize={16}
          className={isLoading ? "animate-spin" : ""}
        >
          {isLoading ? 'Analyzing...' : 'Refresh Analysis'}
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Icon name="FileText" size={24} className="text-primary" />
            <div>
              <p className="text-2xl font-bold text-foreground">{summaryData?.documentsAnalyzed || 0}</p>
              <p className="text-sm text-muted-foreground">Documents Analyzed</p>
            </div>
          </div>
        </div>

        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Icon name="AlertTriangle" size={24} className="text-orange-500" />
            <div>
              <p className="text-2xl font-bold text-foreground">{totalFindings}</p>
              <p className="text-sm text-muted-foreground">Total Findings</p>
            </div>
          </div>
        </div>

        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Icon name="CheckCircle" size={24} className="text-green-500" />
            <div>
              <p className="text-2xl font-bold text-foreground">{resolvedFindings}</p>
              <p className="text-sm text-muted-foreground">Resolved Issues</p>
            </div>
          </div>
        </div>

        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Icon name="TrendingUp" size={24} className="text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-foreground">{completionRate}%</p>
              <p className="text-sm text-muted-foreground">Completion Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-4">Issues by Category</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {summaryData?.categories?.map((category) => (
            <div
              key={category?.type}
              className={`border rounded-lg p-4 ${getSeverityColor(category?.severity)}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon name={getSeverityIcon(category?.type)} size={18} />
                  <span className="font-medium">{getTypeLabel(category?.type)}</span>
                </div>
                <span className="text-sm opacity-75">
                  {category?.severity?.toUpperCase()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold">{category?.count}</span>
                <span className="text-sm">
                  {category?.resolved}/{category?.count} resolved
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalysisSummaryHeader;